
import { useState, useEffect } from 'react';
import { Exercise } from '@/models/LearnTypes';

interface UseExerciseCoreProps {
  exercises: Exercise[];
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>;
}

interface UseExerciseCoreReturn {
  currentExerciseIndex: number;
  showAnswer: boolean;
  userInput: string;
  isInputCorrect: boolean | null;
  wordsToReview: string[];
  totalOriginalExercises: number;
  totalReviewedExercises: number;
  
  setCurrentExerciseIndex: React.Dispatch<React.SetStateAction<number>>;
  setShowAnswer: React.Dispatch<React.SetStateAction<boolean>>;
  setUserInput: (input: string) => void;
  setIsInputCorrect: React.Dispatch<React.SetStateAction<boolean | null>>;
  setWordsToReview: React.Dispatch<React.SetStateAction<string[]>>;
  setTotalOriginalExercises: React.Dispatch<React.SetStateAction<number>>;
  setTotalReviewedExercises: React.Dispatch<React.SetStateAction<number>>;
  
  currentExercise: Exercise | undefined;
}

export const useExerciseCore = ({
  exercises,
  setExercises,
}: UseExerciseCoreProps): UseExerciseCoreReturn => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isInputCorrect, setIsInputCorrect] = useState<boolean | null>(null);
  const [wordsToReview, setWordsToReview] = useState<string[]>([]);
  const [totalOriginalExercises, setTotalOriginalExercises] = useState(0);
  const [totalReviewedExercises, setTotalReviewedExercises] = useState(0);
  
  const currentExercise = exercises[currentExerciseIndex];
  
  // Handle exercise index changes properly when exercises array changes
  useEffect(() => {
    if (exercises.length > 0) {
      if (currentExerciseIndex >= exercises.length) {
        // If the index is out of bounds, reset to first item
        console.log('Exercise index out of bounds, resetting to 0');
        setCurrentExerciseIndex(0);
      } else if (currentExerciseIndex === exercises.length - 1 && exercises.length > totalOriginalExercises) {
        // If we're at the last exercise AND we have recycled words (exercises.length > totalOriginalExercises)
        // This means we just added a failed word and we're at the last original word
        // Move to the first recycled word which is immediately after the original words
        console.log(`Moving from last original word to first recycled word at index ${totalOriginalExercises}`);
        setCurrentExerciseIndex(totalOriginalExercises);
      }
    }
  }, [exercises, currentExerciseIndex, totalOriginalExercises]);
  
  return {
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    wordsToReview,
    totalOriginalExercises,
    totalReviewedExercises,
    
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    setTotalOriginalExercises,
    setTotalReviewedExercises,
    
    currentExercise
  };
};
